// Client-side auth using Web Crypto API (SHA-256 + HMAC JWT)
const AUTH_KEY = 'prl_admin_auth';
const TOKEN_SECRET = 'prl-secret-key-2025'; // In production, use env variable

export interface AdminUser {
  username: string;
  token: string;
  expiresAt: number;
}

// Hash password using SHA-256
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + 'prl-salt');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Create HMAC JWT-like token
async function createToken(username: string): Promise<string> {
  const encoder = new TextEncoder();
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const now = Math.floor(Date.now() / 1000);
  const payload = btoa(JSON.stringify({ sub: username, iat: now, exp: now + 86400 }));
  const key = await crypto.subtle.importKey(
    'raw', encoder.encode(TOKEN_SECRET), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(`${header}.${payload}`));
  const sigArray = Array.from(new Uint8Array(signature));
  const sig = btoa(String.fromCharCode(...sigArray));
  return `${header}.${payload}.${sig}`;
}

export async function login(username: string, password: string): Promise<AdminUser | null> {
  const hashedInput = await hashPassword(password);
  const storedHash = await hashPassword('admin123'); // Default admin password

  if (username === 'admin' && hashedInput === storedHash) {
    const token = await createToken(username);
    const user: AdminUser = { username, token, expiresAt: Date.now() + 86400000 };
    localStorage.setItem(AUTH_KEY, JSON.stringify(user));
    return user;
  }
  return null;
}

export function logout(): void {
  localStorage.removeItem(AUTH_KEY);
}

export function getAuth(): AdminUser | null {
  const raw = localStorage.getItem(AUTH_KEY);
  if (!raw) return null;
  try {
    const user = JSON.parse(raw) as AdminUser;
    if (Date.now() > user.expiresAt) {
      localStorage.removeItem(AUTH_KEY);
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return getAuth() !== null;
}
